import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Cookies from "universal-cookie";
import { get } from "lodash";

import { CheckUserSubscriptionStatus } from "services/companyAPI";
import { getBackupStatus, isAuthorized, login, logout } from "services/authAPI";
import { registerPushNotification } from "util/Notification";
import { getExchangeRates } from "services/efacturaAPI";
import { cryptoStorage } from "services/crypto";

import { setCompanies } from "./companySlice";
import { store } from "./store";
import { setSubscriptionStatus } from "./subscriptionStatusSlice";
import { setCurrencyData } from "./currencySlice";

function getFormattedDate() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
// export const signinAsync = createAsyncThunk(
// 	'user/signin',
// 	(user, thunk) => login(user.username, user.password, user.code)
// 		.then(res => {message
// 			return res?.user;
// 		})
// 		.then(user => {
// 			return user;
// 		})
// 		.catch(err => {
// 			// console.debug('Failed login');
// 			return thunk.rejectWithValue(err.response.data);
// 		})
// );

const initialState = {
  user: null,
};

export const signinAsync = createAsyncThunk(
  "user/signin",
  async (user, thunkAPI) => {
    try {
      return await login(user.username, user.password, user.code)
        .then((res) => ({ ...res.user, rememberMe: user.rememberMe }))
        .catch((error) => {
          if (!!error?.response?.data?.authenticator)
            return thunkAPI.rejectWithValue(error.response.data);

          let userFriendlyMessage;
          if (error.response) {
            switch (error.response.status) {
              case 400:
                userFriendlyMessage =
                  "Request invalid. Verificați datele introduse.";
                break;
              case 401:
                userFriendlyMessage = error.response.data.error;
                break;
              case 500:
                userFriendlyMessage = "Eroare de server. Încercați mai târziu.";
                break;
              default:
                userFriendlyMessage = "Eroare necunoscută. Încercați din nou.";
            }
            throw new Error(userFriendlyMessage);
          } else {
            throw new Error(
              "Ne pare rău, nu s-a putut stabili conexiunea cu serverul."
            );
          }
        });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        error: error.message || "Network Error",
      });
    }
  }
);

export const signOutAsync = createAsyncThunk(
  "user/signout",
  async (payload, thunkAPI) => {
    try {
      const store = thunkAPI.getState();
      const user = get(store, "userReducer.user", null);
      if (!user) return thunkAPI.rejectWithValue("Invalid user data");

      if (!user.refreshToken) return thunkAPI.rejectWithValue("Undefined session");

      return await logout(user.refreshToken)
        .then((res) => res)
        .catch((error) => {
          if (!!error?.response?.data?.authenticator)
            return thunkAPI.rejectWithValue(error.response.data);

          if (error.response) {
            throw new Error("A intervenit o eroare");
          } else {
            throw new Error(
              "Ne pare rău, nu s-a putut stabili conexiunea cu serverul."
            );
          }
        });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        error: error.message || "Network Error",
      });
    }
  }
);

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setPlan: (state, action) => {
      state.user.plan = action.payload;
    },
    setPlanRestrictions: (state, action) => {
      state.user.plan.planRestrictions = action.payload;
    },
    setNotificationStatus: (state, action) => {
      state.user.notificationStatus = action.payload;
    },
    setUserCompany: (state, action) => {
      state.user.company = action.payload;
    },
    setUserLegalEntity: (state, action) => {
      state.user.legalEntity = action.payload;
    },
    setUpdateCnp: (state, action) => {
      state.user.cnp = action.payload;
    },
    setBackup: (state, action) => {
      state.user.backup = action.payload;
    },
    refreshSession: (state, action) => {
      state.user = {
        ...action.payload.user,
        authorizedAt: new Date().toISOString(),
      };
    },
    signout: (state) => {
      state.user = null;
    },
    setAuthenticator: (state, action) => {
      state.user.hasAuthenticator = action.payload;
    },
    // setHasRequestForPayment: (state, action) => {
    //   state.user.hasRequestForPayment = action.payload;
    // },
    signin: (state, action) => {
      let user = action.payload;
      state.user = user;
    },
    updateUserisDeletionAllowed: (state, action) => {
      state.user.isDeletionAllowed = action.payload;
      // state.user = null;
    },
    updateLastName: (state, action) => {
      state.user.lastName = action.payload;
    },
    updateFirstName: (state, action) => {
      state.user.firstName = action.payload;
    },
    updateTelephone: (state, action) => {
      state.user.telephone = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signinAsync.pending, (state) => { })
      .addCase(signinAsync.fulfilled, (state, action) => {
        let user = action.payload;
        state.user = user;

        if (user.rememberMe) {
          const cookies = new Cookies();
          const maxAge = 60 * 60 * 24 * 30;
          const token = cryptoStorage.encrypt(user.refreshToken);

          cookies.set("session", token, { maxAge: maxAge });
          loadUser(user);
          getUserBackup();
          getExchangeRate();
        }
      })
      .addCase(signinAsync.rejected, (state) => {
        state.user = null;
      })
      .addCase(signOutAsync.rejected, (state) => {
        localStorage.clear();

        const cookies = new Cookies();
        cookies.remove("session");

        state.user = null;
      })
      .addCase(signOutAsync.fulfilled, (state) => {
        localStorage.clear();

        const cookies = new Cookies();
        cookies.remove("session");

        state.user = null;
      });
  },
});

export const unloadUser = () => {
  // console.trace("Called unset user");
};

export const loadUser = (user) => {
  // console.trace("Called set user");
  // if (!user?.token)
  //   return;

  if (!user) return;

  cryptoStorage.setItem("token", user.token);
  cryptoStorage.setItem("backend-key", user.keys.backend);
  cryptoStorage.setItem("efactura-key", user.keys.efactura);
  cryptoStorage.setItem("report-key", user.keys.report);

  registerPushNotification(user);

  CheckUserSubscriptionStatus()
    .then((res) => {
      // console.log("datele din verificare subscriptie sunt:", res)
      store.dispatch(setSubscriptionStatus(res));
    })
    .catch((err) => {
      // console.log("eroare verificare subscriptie:", err);
      store.dispatch(setSubscriptionStatus(null));
    });
};

export const getAuthorizations = (user) => {
  const token = user?.token;
  if (!token) {
    return;
  }

  let [, data] = token.split(".");
  let tokenData = JSON.parse(window.atob(data));
  isAuthorized(user.email)
    .then((res) => {
      let authorizations = res?.authorizations?.map(
        ({ company, planAdministrator, ...auth }) => ({
          ...company,
          ...auth,
          id: company.id,
          authorization: auth.id,
          keys: tokenData.subscriptionKeys,
          backup: tokenData.backup,
          plan: planAdministrator,
        })
      );

      store.dispatch(setCompanies(authorizations));

      // Găsește primul obiect care are `planAdministrator`
      // const planAdminData = res?.authorizations?.find(
      //   (auth) => auth.planAdministrator
      // )?.planAdministrator;

      // if (planAdminData) {
      //   store.dispatch(setPlanRestrictions(planAdminData));
      // }
    })
    .catch((err) => {
      store.dispatch(setCompanies([]));
    });
}

export const getExchangeRate = () => {
  getExchangeRates(getFormattedDate())
    .then((res) => {
      store.dispatch(setCurrencyData(res));
    })
    .catch((err) => {
      console.error("Problem retrieving backup: ", err);
    });
}

export const getUserBackup = () => {
  getBackupStatus()
    .then((res) => {
      store.dispatch(setBackup(res.backup));
    })
    .catch((err) => {
      console.error("Problem retrieving backup: ", err);
      store.dispatch(setBackup(null));
    });
}

export const selectUser = (state) => {
  return state.userReducer.user;
};

export const hasAuthenticator = (state) => {
  return !!state.userReducer.user?.hasAuthenticator;
};

export const {
  signin,
  refreshSession,
  signout,
  setPlan,
  setPlanRestrictions,
  setNotificationStatus,
  setUserLegalEntity,
  setUserCompany,
  setUpdateCnp,
  setBackup,
  setAuthenticator,
  // setHasRequestForPayment,
  updateUserisDeletionAllowed,
  updateLastName,
  updateFirstName,
  updateTelephone,
} = userSlice.actions;

export default userSlice.reducer;
